import { useEffect, useRef, useState } from 'react';
import type { ScreenConfig } from '../../shared/protocol.ts';
import './WelcomeScreen.css';
import { conversationIdeas } from '../../shared/conversationIdeas.ts';

/** Decorative video never owns audio; the poster survives blocked iPad autoplay. */
function BackgroundMovie({video, poster}: {video:string;poster:string}) {
  const element=useRef<HTMLVideoElement>(null);
  const [playing,setPlaying]=useState(false);
  useEffect(()=>{
    const media=window.matchMedia('(prefers-reduced-motion: reduce)');
    const sync=()=>{
      const v=element.current;if(!v)return;
      v.muted=true;v.defaultMuted=true;
      if(media.matches || document.hidden){v.pause();setPlaying(false);}
      else v.play().catch(()=>setPlaying(false));
    };
    sync();media.addEventListener('change',sync);document.addEventListener('visibilitychange',sync);
    return ()=>{media.removeEventListener('change',sync);document.removeEventListener('visibilitychange',sync);};
  },[video]);
  return <div className="welcome-media" aria-hidden="true">
    {poster && <img src={poster} alt="" />}
    {video && <video ref={element} src={video} poster={poster} muted loop playsInline preload="metadata"
      disablePictureInPicture tabIndex={-1} className={playing?'is-playing':''}
      onPlaying={()=>setPlaying(true)} onError={()=>setPlaying(false)} />}
  </div>;
}

function QuestionBubble({fact,index}: {fact:{value:string;label:string;question:string};index:number;period?:string}) {
  return <article className={`welcome-bubble bubble-${index}`} aria-label={`${fact.value} ${fact.label}`}>
    <div className="bubble-topline"><span>جرّب أن تسأل</span><i aria-hidden="true">✦</i></div>
    <strong className="bubble-value">{fact.value}</strong>
    <p className="bubble-label">{fact.label}</p>
    <div className="bubble-question"><span aria-hidden="true">؟</span><p dir="auto">{fact.question}</p></div>
  </article>;
}

export function IdleScreen({config}: {config:ScreenConfig}) {
  const facts=conversationIdeas;
  const [motionPaused,setMotionPaused]=useState(document.hidden);
  useEffect(()=>{const change=()=>setMotionPaused(document.hidden);document.addEventListener('visibilitychange',change);return()=>document.removeEventListener('visibilitychange',change);},[]);
  return <section className={`rifq-welcome${motionPaused ? " motion-paused" : ""}`} dir="rtl" aria-label="رِفْق · مساحة للحوار">
    {config.welcome && <BackgroundMovie video={config.welcome.video} poster={config.welcome.poster} />}
    <div className="welcome-shade" aria-hidden="true" /><div className="welcome-aurora" aria-hidden="true" />
    <header className="welcome-header">
      <div className="welcome-company"><img src="/brand/rafed-logo.svg" alt="رافد RAFED" /><span>رحلة آمنة.<br />عناية أقرب.</span></div>
      <div className="welcome-invitation"><span className="welcome-sound" aria-hidden="true"><i/><i/><i/><i/></span>مساحة للحوار</div>
    </header>
    <main className="welcome-scene">
      <div className="welcome-bubbles bubbles-right">{facts.slice(0,2).map((fact,i)=><QuestionBubble key={i} fact={fact} index={i} period={config.welcome?.period}/>)}</div>
      <div className="welcome-persona">
        {config.logoUrl && <div className="welcome-mark"><img src={config.logoUrl} alt="شعار رِفْق" /></div>}
        <p className="welcome-intro">أهلاً بك، أنا</p>
        <h1>رِفْق</h1>
        <p className="welcome-role">مساعدة رافد الذكية</p>
        <div className="welcome-rule" aria-hidden="true" />
        <h2>سؤالك بداية<br /><em>لحوار أوضح.</em></h2>
        <p className="welcome-description">عن رافد، عن الرحلة، وعن سلامتك.<br />اسأل بطريقتك… ودعنا نفهم الصورة معاً.</p>
      </div>
      <div className="welcome-bubbles bubbles-left">{facts.slice(2,4).map((fact,i)=><QuestionBubble key={i} fact={fact} index={i+2} period={config.welcome?.period}/>)}</div>
    </main>
    <footer className="welcome-footer"><p><span aria-hidden="true">✧</span> اختر فكرة تلهمك… واسأل رِفْق بصوتك</p><small>يمكنك السؤال بطريقتك وبلغتك · لبدء بلاغ، يفتح المقدّم مكالمة الدعم</small></footer>
  </section>;
}
